import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { Painel } from '../../../../components/Painel';

import { api } from '../../../../services/api';
import { useAuth } from '../../../../hooks/auth';

import { StatCard } from '../../../../components/StatCard';
import { AttendanceChart } from './components/AttendanceChart';
import { FrequencyThermometer } from './components/FrequencyThermometer';
import { RecentActivity } from './components/RecentActivity';
import { TrainingSpotlight } from './components/TrainingSpotlight';

import {
  BillingStatsRow,
  Content,
  HeaderRow,
  MainGrid,
  PageSubtitle,
  PageTitle,
  PeriodButton,
  PeriodToggle,
  Sidebar,
  StatsRow,
} from './styles';

const BILL_STATUS_LABEL: Record<string, string> = {
  approved: 'Paga',
  waiting: 'Aguardando pagamento',
};

interface ISubscriptionData {
  status: string;
  due_date_at: number;
  subscription: {
    name: string;
    max_users: number | null;
    max_exercises: number | null;
  };
}

interface IBill {
  price: number;
  status: string;
  due_at: string;
  paid_at: string | null;
}

interface IWeeklyFrequencyItem {
  day_of_week: number;
  label: string;
  value: number;
}

interface IRecentActivityItem {
  type: 'new_user' | 'training_completed';
  user_fullname: string;
  training_label?: string;
  occurred_at: string;
}

interface IResponseProps {
  exercisesRegistered: number;
  usersRegistered: number;
  usersActiveInCurrentWeek: number;
  weeklyEngagementPercentage: number;
  weeklyFrequency: IWeeklyFrequencyItem[];
  recentActivity: IRecentActivityItem[];
  usersWithExpiringTraining: number;
}

// TODO: Mocado — a API não distingue visão diária de semanal hoje, só retorna o agregado da semana.
const capitalize = (value: string): string =>
  value.charAt(0).toUpperCase() + value.slice(1);

const Dashboard: React.FC = () => {
  const [data, setData] = useState<IResponseProps>();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'daily' | 'weekly'>('weekly');
  const [subscriptionData, setSubscriptionData] =
    useState<ISubscriptionData | null>(null);
  const [nextBill, setNextBill] = useState<IBill | null>(null);

  const navigation = useNavigate();
  const { user } = useAuth();

  const isGymAdmin = !!user?.permissions?.includes('manage_gym_profile');

  const formattedDate = useMemo(() => {
    const now = new Date();
    const weekday = format(now, 'EEEE', { locale: ptBR }).split('-')[0];
    const day = format(now, 'dd');
    const month = format(now, 'MMM', { locale: ptBR }).replace('.', '');

    return `${capitalize(weekday)}, ${day} ${capitalize(month)}`;
  }, []);

  useEffect(() => {
    api
      .get('gyms/dashboard')
      .then(response => {
        setData(response.data);
      })
      .catch(err => {
        console.log(err?.response?.status);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!isGymAdmin) {
      return;
    }

    api
      .get('subscriptions/current')
      .then(response => setSubscriptionData(response.data))
      .catch(() => setSubscriptionData(null));

    api
      .get('payments/next')
      .then(response => setNextBill(response.data))
      .catch(() => setNextBill(null));
  }, [isGymAdmin]);

  const usersLimitIsUnlimited =
    !!subscriptionData && subscriptionData.subscription.max_users === null;

  const planUsageLabel = subscriptionData
    ? usersLimitIsUnlimited
      ? `${data?.usersRegistered ?? 0}`
      : `${data?.usersRegistered ?? 0}/${
          subscriptionData.subscription.max_users
        }`
    : '—';

  const exercisesLimitIsUnlimited =
    !!subscriptionData && subscriptionData.subscription.max_exercises === null;

  const exercisesUsageLabel = subscriptionData
    ? exercisesLimitIsUnlimited
      ? `${data?.exercisesRegistered ?? 0}`
      : `${data?.exercisesRegistered ?? 0}/${
          subscriptionData.subscription.max_exercises
        }`
    : '—';

  const nextBillNote = !isGymAdmin
    ? 'Apenas para administradores'
    : nextBill
    ? nextBill.status === 'approved'
      ? `Paga em ${format(new Date(nextBill.paid_at as string), 'dd/MM/yyyy')}`
      : `Vence em ${format(new Date(nextBill.due_at), 'dd/MM/yyyy')}`
    : 'Nenhuma fatura encontrada';

  return (
    <Painel loading={loading}>
      <Content>
        <HeaderRow>
          <div>
            <PageTitle>Central de Performance</PageTitle>
            <PageSubtitle>Visão geral · {formattedDate}</PageSubtitle>
          </div>

          {/* <PeriodToggle>
            <PeriodButton
              type="button"
              active={period === 'daily'}
              onClick={() => setPeriod('daily')}
            >
              Diário
            </PeriodButton>
            <PeriodButton
              type="button"
              active={period === 'weekly'}
              onClick={() => setPeriod('weekly')}
            >
              Semanal
            </PeriodButton>
          </PeriodToggle> */}
        </HeaderRow>

        <StatsRow>
          <FrequencyThermometer
            percentage={data?.weeklyEngagementPercentage ?? 0}
            activeCount={data?.usersActiveInCurrentWeek ?? 0}
            totalCount={data?.usersRegistered ?? 0}
            onClick={() => navigation('/users')}
          />
        </StatsRow>

        <BillingStatsRow>
          <StatCard
            title="Uso do plano"
            data={planUsageLabel}
            badge={usersLimitIsUnlimited ? 'Ilimitado' : undefined}
            note="Alunos utilizados"
            accent="primary"
            onClick={() => navigation('/gym/subscriptions')}
          />
          <StatCard
            title="Uso do catálogo de exercícios"
            data={exercisesUsageLabel}
            badge={exercisesLimitIsUnlimited ? 'Ilimitado' : undefined}
            note="Exercícios personalizados criados"
            accent="secondary"
            onClick={() => navigation('/exercises')}
          />
          <StatCard
            title="Próxima fatura"
            data={nextBill ? `R$ ${nextBill.price.toFixed(2)}` : '—'}
            note={nextBillNote}
            accent="tertiaryContainer"
            onClick={() => navigation('/gym/payments')}
          />
          <StatCard
            title="Fichas expirando"
            data={data?.usersWithExpiringTraining ?? 0}
            note="Alunos com ficha expirada ou prestes a expirar"
            accent="primary"
            onClick={() =>
              navigation('/trainings?training_expiration=expired_or_expiring')
            }
          />
        </BillingStatsRow>

        <MainGrid>
          <AttendanceChart
            data={data?.weeklyFrequency || []}
            totalUsers={data?.usersRegistered || 0}
          />
          <Sidebar>
            <RecentActivity activities={data?.recentActivity || []} />
          </Sidebar>
        </MainGrid>
      </Content>
    </Painel>
  );
};

export { Dashboard };
